import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Banner, Button, Input, Spinner } from "@cinesys/ui/components";
import {
  getDocument,
  GlobalWorkerOptions,
  PasswordResponses,
} from "pdfjs-dist";
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist";
import { ZoomIn, ZoomOut } from "react-feather";
import {
  canSubmit,
  initialPasswordAttemptState,
  onIncorrectPassword,
  submit,
} from "../pdfPasswordAttempt";
import { retryAsync } from "../retryAsync";

// Same resolution trick Editor.tsx uses for Monaco's `vs` path: derive a same-origin static
// base path from the currently-executing script's own src, so this works from both the dev
// server and the built production bundle without a CDN dependency. craco.config.js copies
// pdfjs-dist's build/pdf.worker.min.mjs + cmaps/ + standard_fonts/ (as siblings) under this
// path, renaming the worker to .js -- a stock server's mime.types has no .mjs entry and
// serves it as octet-stream, which browsers refuse to run as a module script.
const scriptSrc = (document.currentScript as HTMLScriptElement | null)?.src;
const pdfjsBase =
  scriptSrc?.replace(/static.*$/g, "pdfjs").replace(/main.*$/g, "pdfjs") ?? "";
GlobalWorkerOptions.workerSrc = `${pdfjsBase}/build/pdf.worker.min.js`;
const CMAP_URL = `${pdfjsBase}/cmaps/`;
const STANDARD_FONT_DATA_URL = `${pdfjsBase}/standard_fonts/`;

// Page render is deliberately padding-inclusive fit-to-width -- matches how a document viewer
// reads naturally (scroll vertically through the whole document) rather than shrinking a page
// down to fit a short block.
const CANVAS_PADDING = 24;
const PAGE_GAP = 16;

// 100% = fit-to-width. Bounds/step match the granularity of a typical zoom control.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

// How far past the visible edge a page starts rendering -- large enough that fast scrolling
// doesn't outrun it and show blank pages, without rendering the entire document up front.
const PREFETCH_MARGIN = "800px 0px";

const DEFAULT_LOAD_ERROR = "Failed to load PDF";

// A transient network blip (see describeLoadError's UnknownErrorException case) can fail any
// one of a large PDF's many chunked Range-request fetches; retrying the whole load a few times
// recovers from that without a page reload. A genuinely broken/unsupported PDF still fails the
// same way every attempt and lands on the error Banner once the budget is spent.
const MAX_LOAD_ATTEMPTS = 3;
const LOAD_RETRY_BASE_DELAY_MS = 750;

// This unlocks a document, it isn't a login -- password managers shouldn't offer to fill or
// save it. `Input` already forces `autoComplete: "off"` and a randomized `name` unconditionally,
// but its prop set is closed (no passthrough for arbitrary/data-* props), so the per-manager
// opt-outs that actually work in practice go on imperatively via the DOM node it exposes through
// `ref` instead.
function suppressPasswordManagers(
  node: HTMLInputElement | HTMLTextAreaElement | null
) {
  if (!node) {
    return;
  }
  node.setAttribute("data-1p-ignore", "true"); // 1Password
  node.setAttribute("data-lpignore", "true"); // LastPass
  node.setAttribute("data-bwignore", "true"); // Bitwarden
  node.setAttribute("data-form-type", "other"); // 1Password's heuristic hint
}

interface PdfJsError {
  name?: string;
  message?: string;
  status?: number;
}

// pdf.js's worker boundary normalizes *every* internal load failure -- an encryption scheme it
// doesn't implement, an unrecoverable xref/trailer, a bad server response -- into one of these
// named exceptions before it ever reaches this promise rejection (see message_handler.js
// wrapReason). Naming the actual one instead of a single generic banner is what makes a report
// like "can't load PDF" self-diagnosing instead of a dead end. PasswordException isn't handled
// here -- registering `onPassword` below means pdf.js resolves the password interactively
// instead of ever throwing it through this path.
function describeLoadError({ name, message, status }: PdfJsError): string {
  switch (name) {
    case "MissingPDFException":
      return "The PDF file could not be found on the server";
    case "UnexpectedResponseException":
      return status
        ? `The server returned an error while fetching the PDF (HTTP ${status})`
        : DEFAULT_LOAD_ERROR;
    case "InvalidPDFException":
      return message
        ? `This file isn't a valid PDF and can't be previewed (${message})`
        : "This file isn't a valid PDF and can't be previewed";
    case "UnknownErrorException":
      // The catch-all -- everything pdf.js can't otherwise classify (e.g. an encryption
      // algorithm it doesn't support) lands here with the real cause in `message`.
      return message ? `${DEFAULT_LOAD_ERROR}: ${message}` : DEFAULT_LOAD_ERROR;
    default:
      return DEFAULT_LOAD_ERROR;
  }
}

const Wrapper = styled.div<{ $height?: string; $width?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: ${(props) => props.$height ?? "100%"};
  width: ${(props) => props.$width ?? "100%"};
  background: var(--dark100);
`;

const CanvasContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${PAGE_GAP}px;
  padding: ${CANVAS_PADDING}px;
`;

const PageWrapper = styled.div<{ $minHeight?: number }>`
  display: flex;
  justify-content: center;
  min-height: ${(props) => props.$minHeight ?? 0}px;

  canvas {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.4);
  }
`;

const StatusArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordPrompt = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 280px;
`;

const PasswordActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ZoomControls = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  border-radius: 24px;
  background: var(--dark300);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
`;

async function renderPageToCanvas(
  page: PDFPageProxy,
  canvas: HTMLCanvasElement,
  targetWidth: number
) {
  const unscaledViewport = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({
    scale: targetWidth / unscaledViewport.width,
  });

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas 2d context unavailable");
  }
  // Backing store must be devicePixelRatio-times the CSS size or Retina displays upscale the
  // bitmap and it renders blurry -- CSS width/height stay at the viewport's own (unscaled)
  // size so layout is unaffected.
  const outputScale = window.devicePixelRatio || 1;
  canvas.width = Math.floor(viewport.width * outputScale);
  canvas.height = Math.floor(viewport.height * outputScale);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;

  const transform =
    outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;
  return page.render({ canvasContext: ctx, viewport, transform });
}

interface PdfPageProps {
  page: PDFPageProxy;
  pageNumber: number;
  containerRef: React.RefObject<HTMLDivElement>;
  targetWidth: number;
}

const PdfPage = ({
  page,
  pageNumber,
  containerRef,
  targetWidth,
}: PdfPageProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);

  const [visible, setVisible] = useState(false);
  const [pageError, setPageError] = useState(false);

  // `page` is already-loaded metadata (see PdfViewer's Promise.all), so the aspect ratio is
  // known synchronously -- no async gap where the placeholder briefly has no reserved height
  // and pages pile up at the top before their real size is known.
  const aspectRatio = useMemo(() => {
    const unscaled = page.getViewport({ scale: 1 });
    return unscaled.height / unscaled.width;
  }, [page]);

  useEffect(() => {
    const el = wrapperRef.current;
    const root = containerRef.current;
    if (!el || !root) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
        }
      },
      { root, rootMargin: PREFETCH_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    if (!visible || !canvasRef.current || targetWidth <= 0) {
      return;
    }
    let cancelled = false;
    setPageError(false);

    renderPageToCanvas(page, canvasRef.current, targetWidth)
      .then((task) => {
        renderTaskRef.current = task;
        return task.promise;
      })
      .catch((e) => {
        // A render task we cancel ourselves (zoom/resize, unmount) rejects by design -- only
        // a genuine failure (bad page data, corrupt stream) is worth surfacing.
        if (
          !cancelled &&
          (e as { name?: string })?.name !== "RenderingCancelledException"
        ) {
          console.error(`[PdfViewer] failed to render page ${pageNumber}`, e);
          setPageError(true);
        }
      });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [visible, targetWidth, page, pageNumber]);

  // Always reserved, not just while unrendered -- the canvas ends up exactly this size once it
  // renders, so keeping the wrapper's own height pinned to it (rather than switching to
  // content-derived height once visible) is what keeps pages from ever collapsing to 0 height
  // and stacking on top of each other during the async render gap.
  const placeholderHeight =
    targetWidth > 0 ? targetWidth * aspectRatio : undefined;

  return (
    <PageWrapper ref={wrapperRef} $minHeight={placeholderHeight}>
      {!visible && !pageError && <Spinner />}
      {pageError && <Banner error={`Failed to render page ${pageNumber}`} />}
      <canvas
        ref={canvasRef}
        style={{ display: visible && !pageError ? "block" : "none" }}
      />
    </PageWrapper>
  );
};

export interface PdfViewerProps {
  /** Same-origin URL pdf.js fetches the file bytes from (session-cookie-authenticated). */
  url: string;
  height?: string;
  width?: string;
}

const PdfViewer = ({ url, height, width }: PdfViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  // Points at the *current* effect run's own cancellation flag -- each run gets its own fresh
  // object (see the effect below), so a stale run's `.then`/`.catch` closures keep checking
  // their own flag even after a later run has replaced what this ref points to. Only used
  // imperatively, by handleCancel, to reach into whichever run is currently active.
  const activeRunRef = useRef<{ cancelled: boolean } | null>(null);
  const loadingTaskRef = useRef<ReturnType<typeof getDocument> | null>(null);
  const passwordCallbackRef = useRef<((password: string) => void) | null>(null);

  const [pages, setPages] = useState<PDFPageProxy[]>([]);
  const [status, setStatus] = useState<
    "loading" | "ready" | "error" | "password" | "cancelled"
  >("loading");
  const [errorMessage, setErrorMessage] = useState(DEFAULT_LOAD_ERROR);
  const [passwordReason, setPasswordReason] = useState<
    "needed" | "incorrect" | null
  >(null);
  // `submitted` is the plaintext pdf.js is currently validating (or null) -- see
  // pdfPasswordAttempt.ts for the submit/incorrect transitions. Must not outlive a successful
  // unlock, a cancel, or the prompt itself: nothing should hold the password once it's no
  // longer needed to answer pdf.js's `onPassword` callback.
  const [passwordAttempt, setPasswordAttempt] = useState(
    initialPasswordAttemptState
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [retryToken, setRetryToken] = useState(0);

  // Load the document, then every page's metadata (cheap -- page-tree info, not content
  // decode) up front, whenever the URL changes. Loading all pages before marking 'ready' is
  // what lets every page know its own aspect ratio synchronously, with no async gap where
  // placeholders have no reserved height yet. `retryToken` re-runs this after a cancelled
  // password prompt is reopened, since a destroyed loading task can't be reused.
  useEffect(() => {
    // Own flag per run, not a shared ref -- React runs this run's setup right after the prior
    // run's cleanup in the same flush, so a shared ref would already read `false` again by the
    // time an old, still-in-flight `loadingTask` (e.g. one destroyed mid-password-prompt) settles,
    // letting its rejection clobber whatever this run has since set.
    const runState = { cancelled: false };
    activeRunRef.current = runState;
    setStatus("loading");
    setErrorMessage(DEFAULT_LOAD_ERROR);
    setPasswordReason(null);
    setPasswordAttempt(initialPasswordAttemptState);
    passwordCallbackRef.current = null;
    setPages([]);
    setZoomLevel(1);

    // One attempt: a fresh getDocument() call, wired the same way every time. pdf.js's
    // interactive password flow (onPassword) resolves this same task rather than rejecting it,
    // so it never triggers a retry -- only a genuine load failure does.
    const loadOnce = () => {
      const loadingTask = getDocument({
        url,
        cMapUrl: CMAP_URL,
        cMapPacked: true,
        standardFontDataUrl: STANDARD_FONT_DATA_URL,
      });
      loadingTaskRef.current = loadingTask;

      // Fires once per password attempt -- `reason` distinguishes the first prompt from a retry
      // after a wrong password. Not persisted anywhere; entered fresh on every load.
      loadingTask.onPassword = (
        updatePassword: (password: string) => void,
        reason: number
      ) => {
        if (runState.cancelled) {
          return;
        }
        passwordCallbackRef.current = updatePassword;
        const incorrect = reason === PasswordResponses.INCORRECT_PASSWORD;
        setPasswordReason(incorrect ? "incorrect" : "needed");
        setStatus("password");
        if (incorrect) {
          // Clear in the same render as the "incorrect" relabel, not eagerly on submit -- an
          // eager clear leaves the field blank for a beat before pdf.js confirms the attempt was
          // wrong.
          setPasswordAttempt(onIncorrectPassword);
        }
      };

      return loadingTask.promise;
    };

    retryAsync(loadOnce, {
      attempts: MAX_LOAD_ATTEMPTS,
      delayMs: (failedAttempt) => LOAD_RETRY_BASE_DELAY_MS * failedAttempt,
      isCancelled: () => runState.cancelled,
    })
      .then(async (doc) => {
        if (runState.cancelled) {
          doc.destroy();
          return;
        }
        // Unlocked -- the password isn't needed again for this load, so nothing should go on
        // holding the plaintext.
        setPasswordAttempt(initialPasswordAttemptState);
        docRef.current = doc;
        const loadedPages = await Promise.all(
          Array.from({ length: doc.numPages }, (_, i) => doc.getPage(i + 1))
        );
        if (runState.cancelled) {
          return;
        }
        setPages(loadedPages);
        setStatus("ready");
      })
      .catch((e) => {
        // A cancelled password prompt destroys the loading task itself, which rejects this
        // same promise -- already handled by handleCancel, not a real load failure.
        if (runState.cancelled) {
          return;
        }
        const pdfJsError = (e as PdfJsError | undefined) ?? {};
        console.error(
          `[PdfViewer] failed to load PDF (${pdfJsError.name ?? "unknown"}: ${
            pdfJsError.message ?? e
          })`,
          e
        );
        setErrorMessage(describeLoadError(pdfJsError));
        setStatus("error");
      });

    return () => {
      runState.cancelled = true;
      // Whichever attempt is currently in flight -- retries replace this ref on each new
      // getDocument() call, so it's never the stale first attempt.
      loadingTaskRef.current?.destroy();
      docRef.current?.destroy();
      docRef.current = null;
    };
  }, [url, retryToken]);

  const handleUnlock = () => {
    // Blocking a second submit while one's already in flight (rather than just guarding the
    // eventual response) is what keeps at most one attempt ever outstanding -- see
    // pdfPasswordAttempt.ts.
    if (!canSubmit(passwordAttempt)) {
      return;
    }
    passwordCallbackRef.current?.(passwordAttempt.input);
    setPasswordAttempt(submit);
  };

  const handleCancel = () => {
    if (activeRunRef.current) {
      activeRunRef.current.cancelled = true;
    }
    loadingTaskRef.current?.destroy();
    passwordCallbackRef.current = null;
    setPasswordAttempt(initialPasswordAttemptState);
    setStatus("cancelled");
  };

  const handleReopenPasswordPrompt = () => setRetryToken((t) => t + 1);

  // Track the scroll container's width so every page's fit-to-width render stays in sync.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    setContainerWidth(el.clientWidth);
    const resizeObserver = new ResizeObserver(() =>
      setContainerWidth(el.clientWidth)
    );
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
    // `containerRef` only attaches once CanvasContainer mounts (status === 'ready') -- rerun
    // this effect on status change so the observer doesn't miss that transition.
  }, [status]);

  const targetWidth =
    zoomLevel * Math.max(containerWidth - CANVAS_PADDING * 2, 100);

  return (
    <Wrapper $height={height} $width={width}>
      {status === "loading" && (
        <StatusArea>
          <Spinner />
        </StatusArea>
      )}
      {status === "error" && (
        <StatusArea>
          <Banner error={errorMessage} />
        </StatusArea>
      )}
      {status === "password" && (
        <StatusArea>
          <PasswordPrompt>
            <Input
              ref={suppressPasswordManagers}
              type="password"
              label={
                passwordReason === "incorrect"
                  ? "Incorrect password -- try again"
                  : "This PDF requires a password"
              }
              value={passwordAttempt.input}
              onChange={(input) => setPasswordAttempt((s) => ({ ...s, input }))}
              onSubmit={handleUnlock}
              invalid={passwordReason === "incorrect"}
              autoFocus
            />
            <PasswordActions>
              <Button
                label="Cancel"
                type="danger"
                borderless
                onClick={handleCancel}
              />
              <Button
                label="Unlock"
                onClick={handleUnlock}
                disabled={!canSubmit(passwordAttempt)}
              />
            </PasswordActions>
          </PasswordPrompt>
        </StatusArea>
      )}
      {status === "cancelled" && (
        <StatusArea>
          <PasswordPrompt>
            <Banner placeholder="Password entry cancelled" />
            <PasswordActions>
              <Button
                label="Enter password"
                onClick={handleReopenPasswordPrompt}
              />
            </PasswordActions>
          </PasswordPrompt>
        </StatusArea>
      )}
      {status === "ready" && (
        <>
          <CanvasContainer ref={containerRef}>
            {pages.map((page, i) => (
              <PdfPage
                key={i + 1}
                page={page}
                pageNumber={i + 1}
                containerRef={containerRef}
                targetWidth={targetWidth}
              />
            ))}
          </CanvasContainer>
          <ZoomControls>
            <Button
              icon={ZoomIn}
              borderless
              tooltip="Zoom in"
              disabled={zoomLevel >= ZOOM_MAX}
              onClick={() =>
                setZoomLevel((z) =>
                  Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2))
                )
              }
            />
            <Button
              icon={ZoomOut}
              borderless
              tooltip="Zoom out"
              disabled={zoomLevel <= ZOOM_MIN}
              onClick={() =>
                setZoomLevel((z) =>
                  Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2))
                )
              }
            />
          </ZoomControls>
        </>
      )}
    </Wrapper>
  );
};

export default PdfViewer;
